import { Injectable, inject } from '@angular/core';
import { Observable, EMPTY, firstValueFrom } from 'rxjs';
import { tap, catchError, map } from 'rxjs/operators';
import { withTransaction, transaction, EntityActions, EntityAction } from '@datorama/akita';
import { Router, ParamMap, Params, convertToParamMap, Route } from '@angular/router';
import { RegistrationStepStore } from './registration-step.store';
import { RegistrationStepQuery } from './registration-step.query';
import { ApiService } from '../../shared/services/core/api.service';
import { LoggingService } from '../../shared/services/core/logging.service';
import { RegistrationStep } from './models/registration-step.model';
import { WizardType } from '../models/wizard-type';
import { StepType } from '../models/step-type';
import { RegistrationCompleteResult } from '../models/registration-complete-result';
import { RegistrationResponse } from '../models/registration-response';
import { HttpErrorResponse, HttpParams } from '@angular/common/http';
import { ErrorStateService } from '../../shared/services/core/error.service';
import { AuthService } from '../../user/services/auth.service';
import { MemberDetailsRegistration } from '../../account/member/models/member-details-registration.model';
import { AddressModel } from '../../user/models/address.model';
import { ProviderUiActionResponse } from '../../ecash/models/provider-ui-action-response.model';
import { RegistrationPaymentRequest } from '../models/registration-payment-request';
import { GiftCodeModel } from '../../purchase/models/giftcode/gift-code.model';
import { RegistrationPurchaseBasket } from '../registration-basket/models/registration-purchase-basket';
import { REGISTRATION_ROUTES } from '../../routes/registration.routes';

@Injectable({
  providedIn: 'root',
})
export class RegistrationStepService {
  isLoading: boolean = false;
  modulePath: string;
  successPath: string;
  private readonly registrationRoutes = inject(REGISTRATION_ROUTES);

  constructor(
    private registrationStepStore: RegistrationStepStore,
    private registrationStepQuery: RegistrationStepQuery,
    private api: ApiService,
    private logService: LoggingService,
    private errorService: ErrorStateService,
    private router: Router,
    private authService: AuthService
  ) {
    this._calculateShouldSkipStatusesOnEntityChange();
  }

  getStepDataAndHandle(stepType: StepType) {
    let step = this.registrationStepQuery.getStep(stepType);
    if (!step) {
      this.logService.warning('No step data found in the store for step', stepType);
      return;
    }

    if (step !== null && step.ShouldSkip === true) {
      this.logService.info('mark step as completed', step);
      this.registrationStepStore.update(step, { Complete: true, ShouldSkip: true });
      this.logService.info('Skipping step', step);
      this.navigateToNextStep();
    }
  }

  /**
   * Fetch the required registration steps for the given wizard type from the API and the existing
   * data and put it in the store.
   */
  startRegistrationProcess(wizardType: WizardType, queryData?: Params): Observable<null> {
    this.logService.debug('Starting registration process for wizard type', wizardType, queryData);
    return this.api.post<any>('register/start/' + wizardType, null, this._getQueryParamsAsHttpParams(queryData)).pipe(
      withTransaction((response: RegistrationResponse) => {
        this.registrationStepStore.updateIsComplete(false);
        this.registrationStepStore.updateWizardType(wizardType);

        if (response.Steps) {
          this.logService.info('Started wizard process', wizardType);
          this.logService.debug('Set registration steps in the store', response.Steps);
          this.registrationStepStore.set(response.Steps || []);
          this.registrationStepStore.updateQueryData(queryData || response.QueryData);
          this.navigateToNextStep();
        } else {
          this.logService.warning('No registration steps were returned, redirecting to the home page...');
          // @ts-ignore
          this.registrationStepStore.updateWizardType(-1);
          this.router.navigate(['/']);
        }
      }),
      map(() => null),
      catchError((err: HttpErrorResponse) => {
        this.registrationStepStore.set([]);
        this.registrationStepStore.updateIsComplete(false);
        // @ts-ignore
        this.registrationStepStore.updateWizardType(-1);

        if (err.status == 401) {
          this.logService.warning(
            'Start check not passed the customer is logged in status is :' + this.authService.isAuthenticated()
          );
          this.router.navigate(['/']);
        } else if (err.status == 400) {
          this.logService.warning('No in-progress registration could be found for this session...');
        } else if (err.status == 404) {
          this.logService.warning('Wizard is disabled, redirecting...');
          this.router.navigateByUrl('/register/wizardDisabled', { state: { wizardType } });
        } else if (err.status == 503) {
          this.logService.warning('Currently undergoing maintenance, redirecting to maintenance page...');
          this.router.navigate(['/maintenance']);
        }

        return EMPTY;
      })
    );
  }

  /** Fetch the required registration steps for the current process from the API and put the existing data in the store. */
  fetchRegistrationStepsForCurrentWizard(startStep?: StepType): Observable<null> {
    return this.api.getAnySingle<RegistrationResponse>('register/current').pipe(
      withTransaction((response: RegistrationResponse) => {
        this.registrationStepStore.updateIsComplete(false);

        if (response && response.WizardType !== undefined && response.WizardType !== null) {
          this.registrationStepStore.updateWizardType(response.WizardType);
          this.registrationStepStore.updateQueryData(response.QueryData);
        }

        // Add the list of step data to the store
        let registrationSteps: RegistrationStep[] | null = response.Steps
          ? response.Steps.map(
              (step) =>
                ({
                  Step: step.Step,
                  Order: step.Order,
                  Complete: false,
                  Error: step.Error,
                  Data: step.Data,
                  Settings: step.Settings,
                }) as RegistrationStep
            )
          : null;

        if (registrationSteps) {
          this.registrationStepStore.set(registrationSteps);
          if (startStep) {
            this._markPreviousStepsAsComplete(startStep);
          }
        } else {
          this.logService.warning('No registration steps were returned, redirecting to the home page...');
          this.router.navigate(['/']);
        }
      }),
      map(() => null),
      catchError((err: HttpErrorResponse) => {
        if (err.status == 400) {
          this.logService.warning('No in-progress registration could be found for this session so redirect');
          this.registrationStepStore.set([]);
          this.registrationStepStore.updateIsComplete(false);
          // @ts-ignore
          this.registrationStepStore.updateWizardType(-1);
          this.router.navigate(['/']);
        } else {
          this.logService.warning('No in-progress registration could be found for this session...');

          this.registrationStepStore.set([]);
          this.registrationStepStore.updateIsComplete(false);
          // @ts-ignore
          this.registrationStepStore.updateWizardType(-1);
        }
        return EMPTY;
      })
    );
  }

  /**
   * Retrieve the step data for the given step type
   * ===============================================
   * - shouldRunSetUpview will ensure that the stepProcessor set up view is run to retrive data required to display
   * - shouldSaveToStore is not run by default but if the set up returns data that you want to store you will need to enable this
   */
  fetchRegistrationStepForCurrentWizard(
    step: StepType,
    shouldRunSetUpView: boolean = true,
    shouldSaveToStore: boolean = false
  ): Observable<RegistrationStep> {
    return this.api
      .getById<RegistrationStep>(
        'register/step',
        step.toString(),
        new HttpParams().set('setUpView', shouldRunSetUpView.toString())
      )
      .pipe(
        tap((x) => {
          this.logService.debug(`Retrieved registration step for step ${step}`, x);
          if (shouldSaveToStore) {
            this.registrationStepStore.update(step, { Data: x.Data });
          }
          if (x.ShouldSkip === true) {
            this.logService.info('mark step as completed', step);
            this.registrationStepStore.update(step, { Complete: true, ShouldSkip: true });
            this.logService.info('Skipping step', step);
            this.navigateToNextStep();
          }
        })
      );
  }

  /** Retrieve the current registration's basket to be purchased. */
  fetchRegistrationBasketForCurrentWizard(): Observable<RegistrationPurchaseBasket> {
    return this.api.getAnySingle('register/basket');
  }

  /** Post a step's data to the API for validation and to complete the given step. */
  submitRegistrationStepData(
    stepType: StepType,
    data: any,
    shouldNavigateToNextStep: boolean = true,
    successPath: string = ''
  ): Observable<null> {
    this.isLoading = true;
    this.successPath = successPath;
    return this.api.post<any>(`register/step/${stepType}`, data).pipe(
      tap((t) => {
        /**
         * Update all the steps to should skip to false so we have to rerun this
         */
        let response = t as RegistrationCompleteResult;
        if (response.ChangedData != null && response.ChangedData.length > 0) {
          response.ChangedData.forEach((element) => {
            this.registrationStepStore.update(element, { ShouldSkip: false, Complete: false });
          });
        }

        this.registrationStepStore.update(stepType, { Complete: true, Data: data, Error: undefined });

        this.logService.info('Completed registration step type', stepType);
        this.logService.debug('Current registration data state', this.registrationStepQuery.getAll());

        if (shouldNavigateToNextStep) {
          console.log('nav to next step');
          this.navigateToNextStep();
        } else {
          console.log('not nav to next step');
          this.isLoading = false;
        }
      }),
      map(() => null),
      catchError((err: HttpErrorResponse) => {
        this.logService.error('Error on submission of step data for step type', stepType, err);
        this.errorService.clearErrors();

        if (err.status == 404) {
          // registration no longer exists so remove the user
          this.logService.info('registration no longer exists so remove the user', stepType);
          this.registrationStepStore.updateIsComplete(true);
        }

        if (err.error!.Data) {
          try {
            const result = err.error.Data as RegistrationCompleteResult;
            if (result && result.ErrorMessages && Object.keys(result.ErrorMessages).length > 0) {
              for (const key in result.ErrorMessages) {
                if (result.ErrorMessages.hasOwnProperty(key)) {
                  this.errorService.addError(result.ErrorMessages[key]);
                }
              }
            } else if (result.ErrorDescription) {
              this.errorService.addError(result.ErrorDescription);
            }
          } catch {
            this.errorService.addError(err.error.toString());
          }
        } else {
          this.errorService.addError(err.error.Message);
        }
        this.isLoading = false;
        return EMPTY;
      })
    );
  }

  /** Request to complete the current registration process. */
  completeCurrentRegistration(): Observable<null> {
    return this.api.post<any>('register/complete', null).pipe(
      tap((data: any) => {
        this.logService.info('Registration process successfully completed.');
        this.registrationStepStore.updateIsComplete(true);

        if (this.authService.isAuthenticated()) {
          // Remove the auth token if the user is logged in, forcing a new one to be retrieved
          // with the refresh token so that the account_status property is updated.
          this.logService.info('Refreshing auth token...');
          firstValueFrom(this.authService.refreshAccessToken()).then((data) => {
            this.isLoading = false;
            this.authService.loginUsingToken(data.AccessToken, data.RefreshToken, this.successPath || '/');
          });
        } else {
          this.logService.info('Logging in using token');
          this.authService.loginUsingToken(data.ResultModel.AccessToken, data.ResultModel.RefreshToken, this.successPath || '/');
          this.isLoading = false;
        }
      }),
      map(() => null),
      catchError((err: HttpErrorResponse) => {
        if (err.status === 400) {
          try {
            this.logService.error('An error occured when completing the registration process.');
            this.errorService.clearErrors();

            const result = err.error.Data as RegistrationCompleteResult;
            if (result && result.ErrorNumber) {
              // Add the errors to the error service list
              if (result.ErrorMessages && Object.keys(result.ErrorMessages).length > 0) {
                for (const key in result.ErrorMessages) {
                  if (result.ErrorMessages.hasOwnProperty(key)) {
                    this.errorService.addError(result.ErrorMessages[key]);
                  }
                }
              } else {
                this.errorService.addError(result.ErrorDescription);
              }

              // Navigate to the offending step
              const step = this.registrationStepQuery.getStep(result.StepType);
              if (step) {
                this.registrationStepStore.update(result.StepType, {
                  Complete: false,
                  Error: result.ErrorMessages,
                } as Partial<RegistrationStep>);
                this.navigateToStep(result.StepType);
              } else {
                this.logService.info('No matching step could be found for the step that had an error.', result.StepType);

                const finalStep = this.registrationStepQuery.getFinalStep();
                if (!finalStep) {
                  this.logService.error(
                    'Failed to retrieve the final step in the registration process.',
                    this.registrationStepQuery.getAll()
                  );
                } else {
                  this.logService.info('Navigating to the last step in the registration process.');
                  this.navigateToStep(finalStep.Step);
                }
              }
            } else {
              this.logService.error('Failed to determine the format of the error response message.', err.error);
            }
          } catch {
            this.logService.error('Failed to parse the registration completion error response message.', err.error);
          }
        } else {
          this.logService.info('Response not a Bad Request so will be handled by the HttpErrorInterceptor.');
        }
        this.isLoading = false;
        return EMPTY;
      })
    );
  }

  /** Request to make payment for items selected in the registration process. */
  public makeRegistrationPayment(
    billingAddress: AddressModel,
    shippingAddress: AddressModel,
    callbackUrl: string,
    giftCodes: GiftCodeModel[] = [],
    useCardToken: boolean = false
  ): Observable<ProviderUiActionResponse> {
    if (!billingAddress) {
      this.logService.error('Failed to make registration payment as no billing address was provided.');
      return EMPTY;
    }

    if (!callbackUrl) {
      this.logService.error('Failed to make registration payment as no callback URL was provided.');
      return EMPTY;
    }

    return this.api.post('register/payment', {
      BillingAddress: billingAddress,
      ShippingAddress: shippingAddress,
      PortalUrl: callbackUrl,
      GiftCodes: giftCodes,
      UseCardToken: useCardToken,
    } as RegistrationPaymentRequest);
  }

  /** Marks a step as complete or incomplete. */
  setStepCompleteStatus(stepType: StepType, complete: boolean): void {
    this.logService.info('Updating step complete status', stepType, complete);
    this.registrationStepStore.update(stepType, { Complete: complete });
  }

  /** Marks a step to be skipped or not. */
  setStepSkipStatus(stepType: StepType, shouldSkip: boolean): void {
    this.logService.info('Updating step skip status', stepType, shouldSkip);
    this.registrationStepStore.update(stepType, { ShouldSkip: shouldSkip });
  }

  /** Navigate to the route that represents the given step. */
  navigateToStep(stepType: StepType): void {
    this.logService.info(`Navigating to step ${stepType ? StepType[stepType] : null}`, stepType);

    let routeNode = this._getRouteByStep(stepType);

    if (routeNode) {
      this.logService.debug('Matching route node found for next step', routeNode);
      const path = routeNode.path;
      this.logService.debug('Navigating to path:', path);
      this.router.navigate([this.modulePath + path || '/']);
    } else {
      this.logService.error('No route found for registration step', stepType);
      this.router.navigate(['/']);
    }
  }

  /** Navigate to the route that represents the next available step in the registration process. */
  navigateToNextStep(): void {
    let nextStep = this.registrationStepQuery.getNextStep();
    if (nextStep) {
      this.navigateToStep(nextStep.Step);
      this.isLoading = false;
      return;
    }

    this.logService.info('No more steps, completing the current registration process.');
    this.completeCurrentRegistration().toPromise();
  }

  /** Navigate to the route that represents the previous step in the registration process. */
  navigateToPreviousStep(stepType?: StepType): void {
    let previousStep = this.registrationStepQuery.getPreviousStep(stepType);
    if (!previousStep) {
      this.logService.info('There is no previous step in the registration process - redirecting to the home page...');
      this.router.navigate(['/']);
      return;
    }
    this.registrationStepStore.update(stepType, { Complete: false });
    this.logService.info('Navigating to previous step', previousStep.Step);

    this.errorService.clearErrors();
    this.navigateToStep(previousStep.Step);
  }

  /** Navigate to the route that represents the entry point of the given wizard type. */
  navigateToEntryPoint(wizardType: WizardType): void {
    this.logService.info('Navigating to the entry point for registration type', wizardType);

    let routeNode = this._getRouteByWizardType(wizardType);

    if (routeNode) {
      this.logService.debug('Matching route node found for registration entry point', routeNode);
      const path = routeNode.path;
      this.logService.debug('Navigating to path:', path);
      this.router.navigate([this.modulePath + path || '/']);
    } else {
      this.logService.error('No route found for entry point of registration type', wizardType);
      this.router.navigate(['/']);
    }
  }

  private _getMappedRoutes(): Route[] {
    let stack: Route[] = [...this.registrationRoutes];
    let result: Route[] = [];

    while (stack.length !== 0) {
      const node: any = stack.pop();
      if (node) {
        if (node.children && node.children.length > 0) {
          for (let i = node.children.length; i > 0; i--) {
            // Deep copy the child node (so that we don't modify the actual path)
            let childNode = JSON.parse(JSON.stringify(node.children[i - 1]));
            // Replace the relative path with its absolute one
            childNode.path = node.path + '/' + childNode.path;
            stack.push(childNode);
          }
        } else {
          result.push(node);
        }
      }
    }

    return result;
  }

  private _getRouteByStep(stepType: StepType): Route | undefined {
    return this._getMappedRoutes().find((x) => !!x.data && x.data.registrationStep === stepType);
  }

  private _getRouteByWizardType(wizardType: WizardType): Route | undefined {
    return this._getMappedRoutes().find((x) => !!x.data && x.data.wizardType === wizardType);
  }

  /**
   * When refreshing the page we need to mark the previous steps as 'complete' on the client side
   * so that the user doesn't have to start from the beginning and resubmit all previous steps.
   */
  @transaction()
  private _markPreviousStepsAsComplete(stepType: StepType): void {
    const previousSteps = this.registrationStepQuery.getPreviousSteps(stepType);
    this.logService.debug('Marking previous steps as complete', previousSteps);
    if (previousSteps) {
      for (let step of previousSteps) {
        this.registrationStepStore.update(step.Step, { Complete: true });
      }
    }
  }

  /**
   * Subscribes to the Akita Query to recalculate the skip status of relevant steps when an entity is
   * updated, added or removed from the store.
   *
   * @summary This logic cannot be added in the akitaPreUpdateEntity/akitaPreAddEntity methods of the
   * store as some logic depends on data from other steps. This would require the query class to be
   * injected into the store which causes a circular dependency, so instead we subscribe to any entity
   * actions made when constructing this service and calculate any updates when these events fire.
   */
  private _calculateShouldSkipStatusesOnEntityChange() {
    this.registrationStepQuery.selectEntityAction().subscribe((action: EntityAction<StepType>) => {
      action.ids.forEach((step) => {
        switch (step) {
          case StepType.PersonalDetails:
            const personalDetailsStep = this.registrationStepQuery.getStep(step);
            const personalDetailsStepData = personalDetailsStep ? (personalDetailsStep.Data as MemberDetailsRegistration) : null;
            if (action.type === EntityActions.Remove) {
              this.logService.info('Personal details step removed - setting skip status of shipping address step', false);
              this.registrationStepStore.update(StepType.ShippingAddress, { ShouldSkip: false });
            } else if (personalDetailsStepData) {
              this.logService.info(
                'Personal details step updated - setting skip status of shipping address step',
                !personalDetailsStepData.ShouldUseShippingAddress
              );
              this.registrationStepStore.update(StepType.ShippingAddress, {
                ShouldSkip: !personalDetailsStepData.ShouldUseShippingAddress,
              });
            } else {
              this.logService.info('Personal details step updated - no personal details step data...');
            }
            break;
          // Any other skip logic for steps should be added as a case block here
        }
      });
    });
  }

  /** Get the given ParamMap as a HttpParams object. */
  private _getQueryParamsAsHttpParams(queryParams?: Params): HttpParams | undefined {
    if (!queryParams) {
      return undefined;
    }

    let httpParams = new HttpParams();
    const queryParamMap: ParamMap = convertToParamMap(queryParams);

    queryParamMap.keys.forEach((key) => {
      const value = queryParamMap.get(key);
      httpParams = value ? httpParams.set(key, value) : httpParams;
    });

    return httpParams;
  }

  clearQueryData() {
    this.registrationStepStore.update({ queryData: undefined });
  }
}
